<!-- Global active-call surface as a FLOATING, draggable window (not a blocking
     overlay) so the user can keep navigating the ERP during a call. Mounted
     once in the main layout. Drag by the title bar; collapse / enlarge /
     fullscreen / hang up from the bar. Also hosts the "Rejoin" chip shown
     when a call this tab left (or lost on a reload) is still going on. -->
<template>
  <div>
    <div v-if="active" ref="float" class="call-float"
         :class="{ 'is-min': minimized, 'is-fullscreen': fullscreen }" :style="floatStyle">
      <div class="call-float__bar" @mousedown="startDrag" @dblclick="toggleLarge">
        <span class="call-float__title">
          <span class="call-float__dot" :class="socketConnected ? 'ok' : 'off'"
                :title="socketConnected ? 'Connected' : 'Reconnecting to the server…'"></span>
          <feather-icon icon="VideoIcon" svgClasses="w-4 h-4 mr-1" />
          Call · {{ count }}
          <span class="call-float__time">{{ duration }}</span>
          <span v-if="!socketConnected" class="call-float__warn">reconnecting…</span>
        </span>
        <div class="call-float__actions" @mousedown.stop @dblclick.stop>
          <button :title="minimized ? 'Show the video' : 'Collapse to the bar'" @click="minimized = !minimized">
            <feather-icon :icon="minimized ? 'ChevronUpIcon' : 'ChevronDownIcon'" svgClasses="w-4 h-4" />
          </button>
          <button v-if="!minimized && !fullscreen" :title="large ? 'Normal size' : 'Enlarge'" @click="toggleLarge">
            <feather-icon :icon="large ? 'Minimize2Icon' : 'Maximize2Icon'" svgClasses="w-4 h-4" />
          </button>
          <button v-if="!minimized && canFullscreen" :title="fullscreen ? 'Exit full screen' : 'Full screen'" @click="toggleFullscreen">
            <feather-icon :icon="fullscreen ? 'MinimizeIcon' : 'MaximizeIcon'" svgClasses="w-4 h-4" />
          </button>
          <button class="hangup" title="Leave the call" @click="hangup">
            <feather-icon icon="PhoneOffIcon" svgClasses="w-4 h-4" />
          </button>
        </div>
      </div>
      <div v-show="!minimized" class="call-float__body">
        <call-window />
      </div>

      <!-- Guard against an accidental hang-up: nothing leaves the call without
           an explicit confirmation. Lives inside the float so it stays above the
           video grid whatever the z-index stack elsewhere. -->
      <div v-if="confirmLeave" class="call-confirm">
        <div class="call-confirm__box">
          <feather-icon icon="PhoneOffIcon" svgClasses="w-8 h-8 mb-2" />
          <p class="call-confirm__title">Leave the call?</p>
          <p class="call-confirm__hint">The other participants stay connected. You can rejoin while the call goes on.</p>
          <div class="call-confirm__actions">
            <button class="btn-stay" @click="cancelLeave">Stay</button>
            <button class="btn-leave" @click="confirmHangup">Leave</button>
          </div>
        </div>
      </div>
    </div>

    <!-- A call this tab can get back into (reload, lost socket, left a group call). -->
    <transition name="call-rejoin-fade">
      <div v-if="rejoinable && !active" class="call-rejoin">
        <span class="call-rejoin__pulse"><feather-icon icon="PhoneCallIcon" svgClasses="w-4 h-4" /></span>
        <span class="call-rejoin__text">
          <strong>Call in progress</strong>
          <small v-if="rejoinNames">with {{ rejoinNames }}</small>
        </span>
        <button class="call-rejoin__go" @click="rejoin">Rejoin</button>
        <button class="call-rejoin__close" title="Dismiss" @click="dismissRejoin">
          <feather-icon icon="XIcon" svgClasses="w-4 h-4" />
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import meeting from '@/services/meeting.js'
import CallWindow from '@/views/meeting/components/CallWindow.vue'

export default {
  components: { CallWindow },
  data () {
    return {
      minimized: false,
      large: false,
      fullscreen: false,
      x: null,
      y: null,
      dragging: false,
      dx: 0,
      dy: 0,
      now: Date.now(),
      clock: null,
      viewportW: window.innerWidth,
      viewportH: window.innerHeight
    }
  },
  computed: {
    active () { return meeting.state.room.status === 'active' },
    confirmLeave () { return meeting.state.confirmLeave },
    socketConnected () { return meeting.state.socketConnected },
    rejoinable () { return meeting.state.rejoinable },
    rejoinNames () { return ((this.rejoinable && this.rejoinable.names) || []).join(', ') },
    count () { return meeting.state.participants.length + 1 },
    duration () {
      const from = meeting.state.room.startedAt
      if (!from) return ''
      const s = Math.max(0, Math.floor((this.now - from) / 1000))
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      const sec = s % 60
      const mm = String(m).padStart(2, '0')
      const ss = String(sec).padStart(2, '0')
      return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
    },
    canFullscreen () { return !!(document.fullscreenEnabled || document.webkitFullscreenEnabled) },
    w () {
      if (this.large) return Math.round(this.viewportW * 0.92)
      return Math.min(this.viewportW - 32, 760)
    },
    h () {
      if (this.large) return Math.round(this.viewportH * 0.88)
      return Math.min(this.viewportH - 90, 520)
    },
    floatStyle () {
      if (this.fullscreen) return { left: 0, top: 0, width: '100%', height: '100%', borderRadius: 0 }
      const s = { width: `${this.w}px` }
      if (!this.minimized) s.height = `${this.h}px`
      if (this.x !== null) {
        s.left = `${this.x}px`
        s.top = `${this.y}px`
        s.right = 'auto'
        s.bottom = 'auto'
      }
      return s
    }
  },
  watch: {
    active (val) {
      if (val) {
        this.large = false
        this.minimized = false
        this.dock()
        this.startClock()
      } else {
        this.stopClock()
        this.exitFullscreen()
      }
    },
    // The confirmation must be readable even if the window was reduced.
    confirmLeave (val) { if (val) this.minimized = false },
    large () { this.$nextTick(this.dock) }
  },
  mounted () {
    if (this.active) {
      this.dock()
      this.startClock()
    }
    window.addEventListener('keydown', this.onKeydown)
    window.addEventListener('resize', this.onResize)
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange)
  },
  beforeDestroy () {
    this.stopDrag()
    this.stopClock()
    window.removeEventListener('keydown', this.onKeydown)
    window.removeEventListener('resize', this.onResize)
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange)
  },
  methods: {
    hangup () { meeting.requestHangup() },
    confirmHangup () { meeting.hangup() },
    cancelLeave () { meeting.cancelHangup() },
    rejoin () { meeting.rejoin() },
    dismissRejoin () { meeting.dismissRejoin() },
    onKeydown (e) {
      // Escape while the prompt is up = stay in the call (safest default).
      if (e.key === 'Escape' && this.confirmLeave) this.cancelLeave()
    },
    onResize () {
      this.viewportW = window.innerWidth
      this.viewportH = window.innerHeight
      this.clamp()
    },
    startClock () {
      this.stopClock()
      this.now = Date.now()
      this.clock = setInterval(() => { this.now = Date.now() }, 1000)
    },
    stopClock () {
      if (this.clock) {
        clearInterval(this.clock)
        this.clock = null
      }
    },
    toggleLarge () { if (!this.fullscreen) this.large = !this.large },
    toggleFullscreen () {
      if (this.fullscreen) return this.exitFullscreen()
      const el = this.$refs.float
      if (!el) return
      const req = el.requestFullscreen || el.webkitRequestFullscreen
      if (req) {
        const p = req.call(el)
        if (p && p.catch) p.catch(() => {})
      }
    },
    exitFullscreen () {
      if (!(document.fullscreenElement || document.webkitFullscreenElement)) return
      const ex = document.exitFullscreen || document.webkitExitFullscreen
      if (ex) {
        const p = ex.call(document)
        if (p && p.catch) p.catch(() => {})
      }
    },
    onFullscreenChange () {
      this.fullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement)
      if (!this.fullscreen) this.$nextTick(this.clamp)
    },
    dock () {
      this.$nextTick(() => {
        this.x = Math.max(12, this.viewportW - this.w - 24)
        this.y = Math.max(12, this.viewportH - this.h - 24)
      })
    },
    clamp () {
      if (this.x === null) return
      this.x = Math.min(Math.max(0, this.x), Math.max(0, this.viewportW - 160))
      this.y = Math.min(Math.max(0, this.y), Math.max(0, this.viewportH - 48))
    },
    startDrag (e) {
      if (this.fullscreen) return
      this.dragging = true
      this.dx = e.clientX - (this.x === null ? 0 : this.x)
      this.dy = e.clientY - (this.y === null ? 0 : this.y)
      window.addEventListener('mousemove', this.onDrag)
      window.addEventListener('mouseup', this.stopDrag)
    },
    onDrag (e) {
      if (!this.dragging) return
      this.x = Math.min(Math.max(0, e.clientX - this.dx), this.viewportW - 160)
      this.y = Math.min(Math.max(0, e.clientY - this.dy), this.viewportH - 48)
    },
    stopDrag () {
      this.dragging = false
      window.removeEventListener('mousemove', this.onDrag)
      window.removeEventListener('mouseup', this.stopDrag)
    }
  }
}
</script>

<style scoped>
.call-float {
  position: fixed;
  z-index: 9000;
  background: #0f0f1e;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 18px 60px rgba(0,0,0,.55);
  border: 1px solid rgba(255,255,255,.08);
  display: flex;
  flex-direction: column;
}
.call-float.is-min { height: auto !important; }
.call-float.is-fullscreen { border: none; }
.call-float__bar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 7px 10px;
  background: linear-gradient(180deg, #20203a, #1b1b30);
  color: #fff;
  cursor: move;
  user-select: none;
  flex-shrink: 0;
}
.call-float__title { display: flex; align-items: center; gap: 4px; font-size: 13px; font-weight: 600; }
.call-float__dot { width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; flex-shrink: 0; }
.call-float__dot.ok { background: #28c76f; box-shadow: 0 0 0 3px rgba(40,199,111,.2); }
.call-float__dot.off { background: #ea5455; box-shadow: 0 0 0 3px rgba(234,84,85,.25); animation: call-blink 1s infinite; }
@keyframes call-blink { 50% { opacity: .35; } }
.call-float__time { margin-left: 8px; font-variant-numeric: tabular-nums; font-weight: 500; color: rgba(255,255,255,.7); }
.call-float__warn { margin-left: 8px; font-size: 11px; font-weight: 500; color: #ff9f43; }
.call-float__actions { display: flex; gap: 4px; }
.call-float__actions button {
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border: none; border-radius: 7px;
  background: rgba(255,255,255,.08); color: #fff; cursor: pointer;
}
.call-float__actions button:hover { background: rgba(255,255,255,.18); }
.call-float__actions button.hangup { background: #ea5455; }
.call-float__actions button.hangup:hover { background: #d63031; }
.call-float__body { flex: 1; min-height: 0; }

.call-confirm {
  position: absolute; inset: 0; z-index: 20;
  display: flex; align-items: center; justify-content: center;
  background: rgba(10, 10, 20, .72);
  backdrop-filter: blur(2px);
}
.call-confirm__box {
  display: flex; flex-direction: column; align-items: center;
  max-width: 320px; padding: 20px 22px; text-align: center;
  background: #1b1b30; color: #fff;
  border: 1px solid rgba(255,255,255,.1); border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,.5);
}
.call-confirm__title { font-size: 15px; font-weight: 600; margin: 0 0 4px; }
.call-confirm__hint { font-size: 12px; color: rgba(255,255,255,.6); margin: 0 0 14px; }
.call-confirm__actions { display: flex; gap: 10px; }
.call-confirm__actions button {
  padding: 8px 16px; border: none; border-radius: 8px;
  font-size: 13px; font-weight: 600; color: #fff; cursor: pointer;
}
.call-confirm__actions .btn-stay { background: rgba(255,255,255,.14); }
.call-confirm__actions .btn-stay:hover { background: rgba(255,255,255,.24); }
.call-confirm__actions .btn-leave { background: #ea5455; }
.call-confirm__actions .btn-leave:hover { background: #d63031; }
.call-float__body >>> .call-window { border-radius: 0; }

/* Rejoin chip */
.call-rejoin {
  position: fixed; right: 24px; bottom: 24px; z-index: 9000;
  display: flex; align-items: center; gap: 12px;
  padding: 10px 12px 10px 14px;
  background: #1b1b30; color: #fff;
  border: 1px solid rgba(255,255,255,.1); border-radius: 999px;
  box-shadow: 0 12px 40px rgba(0,0,0,.45);
}
.call-rejoin__pulse {
  width: 34px; height: 34px; border-radius: 50%; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(40,199,111,.2); color: #28c76f; animation: call-rejoin-pulse 1.6s infinite;
}
@keyframes call-rejoin-pulse {
  0% { box-shadow: 0 0 0 0 rgba(40,199,111,.5); }
  70% { box-shadow: 0 0 0 12px rgba(40,199,111,0); }
  100% { box-shadow: 0 0 0 0 rgba(40,199,111,0); }
}
.call-rejoin__text { display: flex; flex-direction: column; line-height: 1.15; }
.call-rejoin__text strong { font-size: 13px; }
.call-rejoin__text small { font-size: 11px; color: rgba(255,255,255,.65); max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.call-rejoin__go {
  border: none; border-radius: 999px; padding: 8px 16px; cursor: pointer;
  background: #28c76f; color: #fff; font-weight: 600; font-size: 13px;
}
.call-rejoin__go:hover { background: #1fae5e; }
.call-rejoin__close {
  border: none; background: transparent; color: rgba(255,255,255,.6); cursor: pointer;
  width: 28px; height: 28px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center;
}
.call-rejoin__close:hover { background: rgba(255,255,255,.12); color: #fff; }
.call-rejoin-fade-enter-active, .call-rejoin-fade-leave-active { transition: opacity .2s, transform .2s; }
.call-rejoin-fade-enter, .call-rejoin-fade-leave-to { opacity: 0; transform: translateY(8px); }
</style>
